import React, { useState, useEffect } from "react";
import { ArrowRight } from "lucide-react";
import Layout from "../components/ui/Layout";
import { Link } from "react-router-dom";
import ProgressBar from "../components/ui/ProgressBar";
import ScrollToTopButton from "../components/ui/ScrollToTopButton";
import {Plus} from "lucide-react";
import {Minus} from "lucide-react";
import { HSP_PLANET_COVER } from "../data/caseStudyMedia";
import CaseOpen from "../components/ui/CaseOpen";

const HSPPlanet = () => {
  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // expand
  const [expandedIndex, setExpandedIndex] = useState(null);
    const toggleExpand = (index) => {
      setExpandedIndex(expandedIndex === index ? null : index);
    };

// ✅ State for Image Zoom
  const [zoomedImage, setZoomedImage] = useState(null);

  const toggleZoom = (imageSrc) => {
    setZoomedImage(zoomedImage === imageSrc ? null : imageSrc);
  };

// Disable scrolling when zoomed
useEffect(() => {
  if (zoomedImage) {
    document.body.style.overflow = "hidden"; // Prevent scrolling
  } else {
    document.body.style.overflow = "auto"; // Restore scrolling
  }
  return () => (document.body.style.overflow = "auto");
}, [zoomedImage]);

  return (
    <Layout>
      {/* ✅ Fullscreen Image Modal (Standard Market Zoom) */}
      {zoomedImage && (
  <div
    className="fixed inset-0 flex items-center justify-center bg-black z-50"
    onClick={() => setZoomedImage(null)} // Close when clicked
  >
    {/* Zoomed Image with Full-Screen Effect */}
    <img
      src={zoomedImage}
      alt="Zoomed View"
      className="w-auto h-auto max-w-full max-h-full object-contain cursor-zoom-out transition-transform duration-300 scale-100"
    />
  </div>
)}
      {/* Skip to Content Link */}
      <a
        href="#hsp-planet"
        className="sr-only focus:not-sr-only focus:fixed focus:top-0 focus:left-0 focus:bg-white focus:p-4 focus:z-50 focus:text-black focus:underline"
      >
        Skip to Content
      </a>

      {!zoomedImage && <ProgressBar />}
      <ScrollToTopButton />



      <CaseOpen
        src={HSP_PLANET_COVER}
        alt="HSP Planet app showcase"
        eyebrow="Case study · HSP Planet"
        title="High sensitivity is a talent: a community product for highly sensitive people"
        dek="Mobile community and wellness app: 100% task success in testing, concept video with 3k views on Rednote."
        overviewId="hsp-overview"
        meta={[
          { label: "Role", value: "Project lead · UX/UI" },
          { label: "Timeline", value: "6 months, 2024" },
          { label: "Type", value: "Mobile · community" },
          { label: "Team", value: "UX designers" },
        ]}
      />

      <div className="container mx-auto px-6 sm:px-8 md:px-12 lg:px-16 max-w-3xl">
        <section id="hsp-planet" className="py-12 space-y-6">
          <p className="text-base leading-relaxed text-gray-700">
            This project began when I was asked to review an article for Highly Sensitive Refugees. I gathered a team of
            UX designers to redesign the experience, resulting in HSP Planet, a mobile app for Highly Sensitive
            Persons.
          </p>
          <p className="text-base leading-relaxed text-gray-700">
            HSPs represent 15 to 20% of the population. HSP Planet offers a gentle place to share, find wellness
            resources, and connect with people who understand sensitivity needs.
          </p>
        </section>



        {/* Video Section */}
        <section className="py-16">
          <div className="w-full aspect-video rounded-lg overflow-hidden">
            <iframe
              className="w-full h-full"
              src="https://www.youtube.com/embed/no6RWRK3mgE"
              title="HSP Planet UX Case Study - YouTube video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              aria-label="HSP Planet UX Case Study Video - Click to play"
            ></iframe>
          </div>
        </section>





        {/* Solution Sections - Feature Slides */}
        <section className="py-20">
          {/* Slide 1: Journal Feature */}
          <div className="mb-20">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="lg:w-1/2 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-semibold flex-shrink-0">1</div>
                  <h2 className="text-2xl font-bold text-gray-900">Journal: a safe place where thoughts flow</h2>
                </div>
                <p className="text-base leading-relaxed text-gray-700">
                  Three writing modes: free writing, prompts, and grids. A private space for personal reflection with daily inspirations to guide your journey of self-discovery and growth.
                </p>
              </div>
              <div className="lg:w-1/2 flex justify-center">
                <div className="w-full max-w-xs bg-gray-50 rounded-2xl p-5">
                  <img
                    src="https://static.wixstatic.com/media/cc7e8a_abc5ea180b0244e3a382917e348e5f82~mv2.gif"
                    alt="Journal Feature"
                    className="w-full h-auto cursor-zoom-in rounded-lg"
                    onClick={() => toggleZoom("https://static.wixstatic.com/media/cc7e8a_abc5ea180b0244e3a382917e348e5f82~mv2.gif")}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Slide 2: Plaza Feature */}
          <div className="mb-20">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="lg:w-1/2 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-semibold flex-shrink-0">2</div>
                  <h2 className="text-2xl font-bold text-gray-900">Plaza: share your personal story</h2>
                </div>
                <p className="text-base leading-relaxed text-gray-700">
                  Read others' stories and share your self-growth experiences with the community or specific groups. Connect through follow, message, and comment features to build meaningful relationships.
                </p>
              </div>
              <div className="lg:w-1/2 flex justify-center">
                <div className="w-full max-w-xs bg-gray-50 rounded-2xl p-5">
                  <img
                    src="https://static.wixstatic.com/media/cc7e8a_d7cc4fff7338408295147341ecbc211a~mv2.gif"
                    alt="Plaza Feature"
                    className="w-full h-auto cursor-zoom-in rounded-lg"
                    onClick={() => toggleZoom("https://static.wixstatic.com/media/cc7e8a_d7cc4fff7338408295147341ecbc211a~mv2.gif")}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Slide 3: Articles Feature */}
          <div className="mb-20">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="lg:w-1/2 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-semibold flex-shrink-0">3</div>
                  <h2 className="text-2xl font-bold text-gray-900">Articles & more: professional guidance all-in-one</h2>
                </div>
                <p className="text-base leading-relaxed text-gray-700">
                  Access research-based articles, tests, books, and podcasts. Carefully selected professional resources to support your personal development journey with evidence-based insights.
                </p>
              </div>
              <div className="lg:w-1/2 flex justify-center">
                <div className="w-full max-w-xs bg-gray-50 rounded-2xl p-5">
                  <img
                    src="https://static.wixstatic.com/media/cc7e8a_7ae63263d9c24c77b98213f35bddcc2d~mv2.gif"
                    alt="Articles Feature"
                    className="w-full h-auto cursor-zoom-in rounded-lg"
                    onClick={() => toggleZoom("https://static.wixstatic.com/media/cc7e8a_7ae63263d9c24c77b98213f35bddcc2d~mv2.gif")}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Slide 4: Message Feature */}
          <div className="mb-20">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="lg:w-1/2 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-semibold flex-shrink-0">4</div>
                  <h2 className="text-2xl font-bold text-gray-900">Message: discover more possibilities...</h2>
                </div>
                <p className="text-base leading-relaxed text-gray-700">
                  Official notifications keep you informed about community updates and important announcements. Private conversations allow for deeper, more personal connections with other HSPs who share similar experiences and interests.
                </p>
              </div>
              <div className="lg:w-1/2 flex justify-center">
                <div className="w-full max-w-xs bg-gray-50 rounded-2xl p-5 flex items-center justify-center">
                  <img
                    src="https://static.wixstatic.com/media/cc7e8a_2fc7d1dc0b0249ac876705d28d9196b0~mv2.gif"
                    alt="Messaging Feature"
                    className="w-full h-auto cursor-zoom-in rounded-lg"
                    onClick={() => toggleZoom("https://static.wixstatic.com/media/cc7e8a_2fc7d1dc0b0249ac876705d28d9196b0~mv2.gif")}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Figma Prototype Section */}
        <section className="py-16">
          <h2 className="text-2xl text-gray-900 mb-8 text-center"> 👇🏽 Play with it yourself</h2>
          <div className="w-full aspect-video rounded-lg overflow-hidden">
            <iframe
              className="w-full h-full border-0"
              src="https://www.figma.com/embed?embed_host=share&url=https://www.figma.com/proto/65UoP0c86TWCHPAelHV7Z3/HSP-_Young?kind=proto&node-id=1-42144&page-id=1%3A39972&scaling=scale-down&starting-point-node-id=1%3A40246"
              allowFullScreen
              title="HSP Young Figma Prototype"
            ></iframe>
          </div>
        </section>

      </div> {/* End of .container */}

      {/* View Full Process Section */}
      <div className="container mx-auto px-6 sm:px-8 md:px-12 lg:px-16 max-w-7xl py-12">
        <div className="text-center">
          <a
            href="https://jiadiyounghe.wixsite.com/designportfolio/hspplanet"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-primary hover:underline text-lg font-medium transition-all"
          >
            View full process <ArrowRight size={20} />
          </a>
        </div>
      </div>

      {/* Case Study Navigation */}
      <div className="container mx-auto px-6 sm:px-8 md:px-12 lg:px-16 max-w-7xl py-12 border-t border-gray-200">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-6">
          <Link
            to="/DesignSystem"
            className="flex items-center gap-2 text-lg text-primary hover:opacity-70 transition-opacity font-light"
          >
            ← Previous Case Study: Design System
          </Link>
          <Link
            to="/IntranetConnections"
            className="flex items-center gap-2 text-lg text-primary hover:opacity-70 transition-opacity font-light"
          >
            Next Case Study: Intranet Connections →
          </Link>
        </div>
      </div>  
    </Layout>
  );
};

export default HSPPlanet;